import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'

import App from './App.vue'
import router from './router'
import './style.css'

const app = createApp(App)

const pinia = createPinia()

// Sentry — only enabled when DSN is provided (production)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: false,
      }),
    ],
    // Capture 100% of errors and sample 10% of transactions. Replays only around an error, with text masked: the
    // Privacy policy promises no usage tracking without consent, so no session is recorded for its own sake (GET-115).
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0, // Always capture replay on error
  })
}

// After a deploy, a tab still on the old build can fail to load a lazy chunk:
// reload once into the new build. The flag stops a loop if the reload fails too.
const CHUNK_RELOAD_KEY = 'chunk-reload'
window.addEventListener('vite:preloadError', (event) => {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  } catch {
    return
  }
  event.preventDefault()
  window.location.reload()
})

app.use(pinia)
app.use(router)

router.isReady().then(() => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    // storage blocked: the handler above never reloads either
  }
})

app.mount('#app')
